/**
 * Поля-ссылки между сторонами указателя (✏️ 28.09, Паша).
 *
 * Элемент-ссылка шаблона Автокрафта (`template.data.linkTargetHash`) рисует значение другого
 * поля ИЗ ТЕХ ЖЕ ДАННЫХ. У сторон, разведённых типом по страницам, данные свои: ссылка на
 * второй странице (оборот) взяла бы значение поля первой страницы из данных оборота, где это
 * поле никто не заполняет — в его форме оно даже не показано. Поэтому значение поля, на
 * которое ссылается страница стороны, приезжает к ней со стороны, чья страница это поле
 * содержит. Направление любое: откуда ссылка и куда, решает шаблон.
 *
 * Одна логика на панель указателя (правка поля и сверка при открытии), «Обновить все макеты» и
 * выгрузку: иначе у неоткрытого указателя обложка и файл в архиве собирались бы со старым
 * значением ссылки.
 */
import {
  AUTOCRAFT_COVER_SIGNATURE_OUTDATED,
  AUTOCRAFT_COVER_SIGNATURE_PROPERTY,
  AUTOCRAFT_DATA_PROPERTY,
  AUTOCRAFT_GENERATOR_PROPERTY,
  type AutocraftGeneratorSchema,
  type AutocraftTemplatePages,
} from '../types/autocraft.js';
import type { WayfindingSignType } from '../types/wayfinding.js';
import { getAutocraftField } from './autocraftFields.js';
import { buildInitialAutocraftValues, getAutocraftOrderedFieldKeys } from './autocraftPayload.js';
import { holdsForeignAutocraftData } from './wayfindingAutocraftAdoption.js';
import {
  readWayfindingSignSideProperties,
  toWayfindingSignSidePatch,
  wayfindingSignSideCount,
  wayfindingSignSideKey,
  wayfindingSignTypeSidePage,
} from './wayfindingSignSides.js';

/**
 * Ключ поля формы по хешу элемента шаблона. Ссылка шаблона указывает на хеш элемента; ключ
 * поля обычно он же, но бывает и отдельный `elementHash`.
 */
export function autocraftFieldKeyByElement(schema: AutocraftGeneratorSchema): Map<string, string> {
  const out = new Map<string, string>();
  const keys = getAutocraftOrderedFieldKeys(schema);
  for (const key of keys) {
    out.set(key, key);
  }
  // Отдельный `elementHash` важнее совпавшего по имени ключа: ссылка — на элемент.
  for (const key of keys) {
    const elementHash = getAutocraftField(schema, key)?.elementHash;
    if (elementHash) {
      out.set(elementHash, key);
    }
  }
  return out;
}

/**
 * Сторона, чья форма правит поля страницы `page`: первая по номеру из стоящих на ней. Та же,
 * что держит «Общие поля» в панели (`planAutocraftSideFields`): у остальных сторон той же
 * страницы эти поля не показаны, и передавать их устаревшие копии нельзя.
 */
export function autocraftPageOwnerSide(
  sidePages: ReadonlyMap<number, number>,
  page: number,
): number | null {
  let owner: number | null = null;
  for (const [side, sidePage] of sidePages) {
    if (sidePage === page && (owner == null || side < owner)) {
      owner = side;
    }
  }
  return owner;
}

function readData(view: Record<string, unknown>): Record<string, unknown> {
  const data = view[AUTOCRAFT_DATA_PROPERTY];
  return data && typeof data === 'object' && !Array.isArray(data)
    ? (data as Record<string, unknown>)
    : {};
}

interface SideLinksContext {
  /** Страницы сторон, разведённых типом, без сторон с чужими данными. */
  sidePages: ReadonlyMap<number, number>;
  template: AutocraftTemplatePages | null | undefined;
  generatorHash: string;
  /**
   * Схема формы генератора: ключ поля по хешу элемента (`autocraftFieldKeyByElement`) и
   * умолчания. Поле, которое у стороны не сохранено, форма и сборка берут умолчанием — и
   * сравнивать надо с ним, иначе незаполненное поле владельца не доехало бы до стороны со
   * ссылкой, где лежит старое значение. Нет схемы — ключ поля считается хешем элемента, а
   * сравниваются только сохранённые значения.
   */
  schema?: AutocraftGeneratorSchema | null;
}

/** Данные стороны так, как их видят форма и сборка: сохранённое поверх умолчаний схемы. */
function withDefaults(
  schema: AutocraftGeneratorSchema | null | undefined,
  data: Record<string, unknown>,
): Record<string, unknown> {
  return schema ? buildInitialAutocraftValues(schema, data) : data;
}

/**
 * Патч других сторон после записи данных стороны `fromSide`: в данные каждой стороны, чья
 * страница ссылается на поле страницы `fromSide`, кладётся его новое значение, а обложка
 * помечается устаревшей — форма той стороны пересоберёт макет сама (как после выбора
 * конечной точки). Пусто — передавать нечего.
 *
 * Передаёт только сторона-владелец страницы (`autocraftPageOwnerSide`). Сторона с данными
 * другого генератора не трогается: у неё плашка «чужие данные», и решает человек. Стороны без
 * своей страницы в типе (`sidePages`) — тоже: у них все поля свои.
 */
export function linkedAutocraftSidesPatch(
  params: SideLinksContext & {
    properties: Record<string, unknown>;
    fromSide: number;
    /** Данные стороны `fromSide` после записи. */
    data: Record<string, unknown>;
  },
): Record<string, unknown> {
  const { properties, fromSide, sidePages, template, generatorHash, schema } = params;
  const data = withDefaults(schema, params.data);
  const keyByElement = schema ? autocraftFieldKeyByElement(schema) : null;
  const fromPage = sidePages.get(fromSide);
  const fieldPages = template?.fieldPages;
  const linkTargets = template?.linkTargetsByPage;
  if (fromPage == null || !fieldPages || !linkTargets) {
    return {};
  }
  if (autocraftPageOwnerSide(sidePages, fromPage) !== fromSide) {
    return {};
  }
  const patch: Record<string, unknown> = {};
  for (const [side, page] of sidePages) {
    if (side === fromSide || page === fromPage) {
      continue;
    }
    // Поля страницы `fromSide`, на которые ссылается страница этой стороны.
    const keys = (linkTargets[String(page)] ?? []).flatMap((target) => {
      if (fieldPages[target] !== fromPage) {
        return [];
      }
      const key = keyByElement?.get(target) ?? target;
      return key in data ? [key] : [];
    });
    if (keys.length === 0) {
      continue;
    }
    const view = readWayfindingSignSideProperties(properties, side);
    const ownGenerator = view[AUTOCRAFT_GENERATOR_PROPERTY];
    if (ownGenerator != null && ownGenerator !== '' && ownGenerator !== generatorHash) {
      continue;
    }
    const current = readData(view);
    const seen = withDefaults(schema, current);
    const next = { ...current };
    let changed = false;
    for (const key of keys) {
      if (JSON.stringify(seen[key]) !== JSON.stringify(data[key])) {
        next[key] = data[key];
        changed = true;
      }
    }
    if (!changed) {
      continue;
    }
    Object.assign(
      patch,
      toWayfindingSignSidePatch(
        {
          [AUTOCRAFT_GENERATOR_PROPERTY]: generatorHash,
          [AUTOCRAFT_DATA_PROPERTY]: next,
          [AUTOCRAFT_COVER_SIGNATURE_PROPERTY]: AUTOCRAFT_COVER_SIGNATURE_OUTDATED,
        },
        side,
      ),
    );
  }
  return patch;
}

/**
 * Сверка связанных полей всех сторон — при открытии указателя: значения могли разойтись
 * (указатель собран до разводки по страницам, поле правили в таблице). Источник — сторона-
 * владелец страницы, где поле лежит. Пусто — всё сходится.
 */
export function reconcileLinkedAutocraftSides(
  params: SideLinksContext & { properties: Record<string, unknown> },
): Record<string, unknown> {
  const { sidePages, generatorHash } = params;
  let properties = params.properties;
  const patch: Record<string, unknown> = {};
  for (const side of sidePages.keys()) {
    const view = readWayfindingSignSideProperties(properties, side);
    // Данные стороны ещё не заведены или чужие — передавать от неё нечего.
    if (view[AUTOCRAFT_GENERATOR_PROPERTY] !== generatorHash) {
      continue;
    }
    const sidePatch = linkedAutocraftSidesPatch({
      ...params,
      properties,
      fromSide: side,
      data: readData(view),
    });
    if (Object.keys(sidePatch).length > 0) {
      Object.assign(patch, sidePatch);
      // Ключи сторон плоские: следующая сторона видит уже переданные значения.
      properties = { ...properties, ...sidePatch };
    }
  }
  return patch;
}

/**
 * Страницы сторон, между которыми ходят поля-ссылки и по которым раскладываются поля формы:
 * заданные типом (`autocraftPageBySide`), без сторон с заполненными данными другого генератора —
 * их форма не показана. Одно правило на сборку мимо панели и таблицу указателей. Пусто — тип
 * стороны по страницам не разводит.
 */
export function wayfindingLinkedSidePages(
  properties: Record<string, unknown>,
  type: Pick<WayfindingSignType, 'sides' | 'autocraftGeneratorHash' | 'autocraftPageBySide'>,
): Map<number, number> {
  const sidePages = new Map<number, number>();
  const generatorHash = type.autocraftGeneratorHash;
  if (!generatorHash) {
    return sidePages;
  }
  for (let side = 1; side <= wayfindingSignSideCount(type); side += 1) {
    const page = wayfindingSignTypeSidePage(type, side);
    if (page != null && !wayfindingSideHoldsForeignAutocraftData(properties, side, generatorHash)) {
      sidePages.set(side, page);
    }
  }
  return sidePages;
}

/**
 * Сторона указателя хранит заполненные данные не того генератора (`holdsForeignAutocraftData`).
 * Видно по двум ключам стороны — копия всех свойств (`readWayfindingSignSideProperties`) ради
 * них не нужна, а зовётся это на каждую сторону каждого указателя таблицы.
 */
export function wayfindingSideHoldsForeignAutocraftData(
  properties: Record<string, unknown>,
  side: number,
  generatorHash: string,
): boolean {
  return holdsForeignAutocraftData(
    {
      [AUTOCRAFT_GENERATOR_PROPERTY]: properties[wayfindingSignSideKey(AUTOCRAFT_GENERATOR_PROPERTY, side)],
      [AUTOCRAFT_DATA_PROPERTY]: properties[wayfindingSignSideKey(AUTOCRAFT_DATA_PROPERTY, side)],
    },
    generatorHash,
  );
}

/**
 * Свойства указателя со сведёнными полями-ссылками — для сборки мимо панели («Обновить все
 * макеты», выгрузка): ровно то, что свела бы панель при открытии указателя. `changedSides` —
 * стороны, чьи данные поменялись: их готовые обложка и PDF собраны со старым значением.
 *
 * Стороны с заполненными данными другого генератора в ссылках не участвуют — как в панели.
 */
export function withLinkedAutocraftSides(params: {
  properties: Record<string, unknown>;
  type: Pick<WayfindingSignType, 'sides' | 'autocraftGeneratorHash' | 'autocraftPageBySide'>;
  template: AutocraftTemplatePages | null | undefined;
  schema?: AutocraftGeneratorSchema | null;
}): { properties: Record<string, unknown>; changedSides: ReadonlySet<number> } {
  const { properties, type, template, schema } = params;
  const generatorHash = type.autocraftGeneratorHash;
  const unchanged = { properties, changedSides: new Set<number>() };
  if (!generatorHash || !template) {
    return unchanged;
  }
  const sidePages = wayfindingLinkedSidePages(properties, type);
  if (sidePages.size < 2) {
    return unchanged;
  }
  const patch = reconcileLinkedAutocraftSides({
    properties,
    sidePages,
    template,
    generatorHash,
    schema,
  });
  const changedSides = new Set<number>();
  for (const side of sidePages.keys()) {
    if (wayfindingSignSideKey(AUTOCRAFT_DATA_PROPERTY, side) in patch) {
      changedSides.add(side);
    }
  }
  return changedSides.size > 0 ? { properties: { ...properties, ...patch }, changedSides } : unchanged;
}
